import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Flame, Wrench, Snowflake, Factory, Star, Quote, MapPin, Clock } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const Index = () => {
  const services = [
    {
      title: "CV onderhoud",
      icon: <Flame className="h-10 w-10 text-amber-500" />,
      description: "Onderhoud volgens Gasketelwet (BRL 6000-25) voor een efficiënte werking en langere levensduur.",
      link: "/diensten#cv"
    },
    {
      title: "Airco-, koel- en vriesinstallaties",
      icon: <Snowflake className="h-10 w-10 text-amber-500" />,
      description: "Plaatsing, reparatie en onderhoud conform F-gassenverordening (EU 2024/573).",
      link: "/diensten#airco"
    },
    {
      title: "Leidingwerk",
      icon: <Wrench className="h-10 w-10 text-amber-500" />,
      description: "Vakkundig aangelegd, aangepast of hersteld. Alle diameters en diverse materialen.",
      link: "/diensten#leidingwerk"
    },
    {
      title: "Industrie en utiliteitsbouw",
      icon: <Factory className="h-10 w-10 text-amber-500" />,
      description: "Veilig en vakkundig werken in industriële projecten met VCA-certificaat.",
      link: "/diensten#industrie"
    }
  ];

  const reviews = [
    {
      name: "Jeroen Weber",
      text: "Michael heeft bij mij een stuk afvoer aangelegd. Erg tevreden over zijn diensten. Michael werkt precies en nauwkeurig, heeft inhoudelijk kennis van zaken en is doorzichtig in de kosten. Hij is ook flexibel, stuk afvoer bleek in de huidige situatie iets te smal welke hij ter plekke top heeft opgelost. Werkt met goed materiaal. Al met al een vakman die ik blind vertrouw bij de vakklussen",
      rating: 5,
      date: "Recent"
    },
    {
      name: "Bram Weijers",
      text: "Michael heeft mij uitstekend geholpen na een lek in CV leiding. Het begon met het herstellen van de leiding, hiervoor moest schade worden gemaakt in mijn badkamer en plafond. Dit is allemaal keurig hersteld. Michael gaat zeer zorgvuldig, netjes en kalm te werk.",
      rating: 5,
      date: "8 maanden geleden"
    },
    {
      name: "Merel Engelgeer",
      text: "Ik belde smorgens vroeg of er gekeken kon worden naar ons toilet en vaatwasser. Michael kwam dezelfde dag nog om de problemen op te lossen. Hele vriendelijke behulpzame man. Zulke persoonlijke en snelle goeie service krijg je niet vaak meer. Wij bellen voortaan naar M.Pomp Multiservice !!",
      rating: 5,
      date: "7 maanden geleden"
    }
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 opacity-90"></div>
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1581094288338-2314dddb7ece?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
        
        <div className="container mx-auto px-4 py-24 md:py-32 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Vakmanschap in <span className="text-amber-500">Installatietechniek</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
              M Pomp Multiservice is uw betrouwbare partner voor al uw installatiewerk, onderhoud en storingen. Landelijk inzetbaar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Phone Button - explicitly styled for clicks using standard button */}
              <button 
                onClick={() => window.location.href = 'tel:+31625007564'} 
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg h-14 px-8 flex items-center gap-2 cursor-pointer relative z-20 pointer-events-auto border-none"
                )}
              >
                <Phone className="h-5 w-5" />
                Bel: 06 25007564
              </button>
              {/* Quote Button - bg-transparent fixes white-on-white text issue */}
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-slate-900 h-14 px-8 text-lg cursor-pointer pointer-events-auto">
                <Link to="/contact">Offerte Aanvragen</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section (Below CTA) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="flex-1 space-y-6 text-slate-700 leading-relaxed">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Even voorstellen</h2>
              
              <p className="font-medium text-lg text-slate-900">
                Mijn naam is Michael Pomp.
                <br />
                Ik ben gecertificeerd CV-monteur en gecertificeerd koelmonteur.
              </p>

              <p>
                Ik ben in het bezit van het F-gassen categorie 1 diploma en het CO-certificaat, wat betekent dat alle werkzaamheden worden uitgevoerd volgens de laatste normen en veiligheidsvoorschriften.
              </p>

              <p>
                U kunt bij mij terecht voor loodgieterswerk, leidingwerk, storingen, service en onderhoud. Ik werk uitsluitend met moderne gereedschappen en hoogwaardige materialen.
              </p>

              <p>
                Ik verricht werkzaamheden voor bedrijven, instellingen, industrie en particulieren.
                <br />
                Ook voor een volledige toilet- of badkamerrenovatie of herstelwerkzaamheden na bijvoorbeeld lekkage kunt u bij mij terecht. Dankzij mijn brede netwerk van ervaren tegelzetters, timmerlieden en elektriciens kan ik snel schakelen en het complete traject verzorgen.
              </p>

              <p>
                Het voordeel voor u: één vast aanspreekpunt gedurende het hele project.
                <br />
                Eerlijkheid, transparantie en duidelijkheid staan bij mij centraal.
              </p>

              <p className="font-medium">
                Daarnaast ben ik beschikbaar voor bedrijven die tijdelijk extra capaciteit nodig hebben, bijvoorbeeld tijdens piekmomenten, voor specialistisch werk of technische ondersteuning.
              </p>
            </div>
            
            {/* Images */}
            <div className="w-4/5 lg:w-4/12 shrink-0 mx-auto lg:mx-0">
               <div className="grid grid-cols-2 gap-4">
                 <div className="w-full rounded-xl overflow-hidden shadow-md border border-slate-100">
                   <img 
                     src="https://i.imgur.com/frNOTAM.jpeg" 
                     alt="Michael Pomp Portret" 
                     className="w-full h-auto object-contain"
                   />
                 </div>
                 <div className="w-full rounded-xl overflow-hidden shadow-md border border-slate-100">
                   <img 
                     src="https://i.imgur.com/pVmEmDR.jpeg" 
                     alt="Michael Pomp Werkzaamheden" 
                     className="w-full h-auto object-contain"
                   />
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6 rounded-xl bg-white border border-slate-100 shadow-sm">
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-4">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Betrouwbaar Vakmanschap</h3>
              <p className="text-slate-600">Ik sta voor kwaliteit en kom mijn afspraken na. Een eenmanszaak met persoonlijke aandacht.</p>
            </div>
            <div className="p-6 rounded-xl bg-white border border-slate-100 shadow-sm">
              <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 mb-4">
                <Clock className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Ruime Openingstijden</h3>
              <p className="text-slate-600">7 dagen per week bereikbaar van 07:00 tot 19:00 uur. Ook voor spoedgevallen.</p>
            </div>
            <div className="p-6 rounded-xl bg-white border border-slate-100 shadow-sm">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                <MapPin className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Landelijk & Betrokken</h3>
              <p className="text-slate-600">Snel ter plaatse en korte lijnen. Ik ben overal inzetbaar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Mijn Diensten</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Van jaarlijks onderhoud tot specialistisch leidingwerk. Ik ben van alle markten thuis.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-shadow duration-300 bg-slate-50">
                <CardContent className="p-6 flex flex-col items-center text-center h-full">
                  <div className="mb-6 p-4 bg-white rounded-full shadow-sm">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-slate-900 min-h-[3.5rem] flex items-center justify-center">{service.title}</h3>
                  <p className="text-slate-600 mb-6 flex-grow text-sm">
                    {service.description}
                  </p>
                  <Link to={service.link} className="text-amber-600 font-semibold hover:text-amber-700 inline-flex items-center gap-2">
                    Lees meer <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white px-8">
              <Link to="/diensten">Bekijk alle diensten</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Wat klanten zeggen</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Ik ben pas tevreden als u dat bent. Lees de ervaringen van mijn klanten.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <Card key={index} className="bg-white border border-slate-100 shadow-sm">
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="flex mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <div className="mb-6 relative">
                    <Quote className="h-8 w-8 text-slate-200 absolute -top-2 -left-2 -z-10" />
                    <p className="text-slate-700 italic relative z-10 leading-relaxed">
                      "{review.text}"
                    </p>
                  </div>
                  <div className="mt-auto pt-4 border-t border-slate-200">
                    <p className="font-bold text-slate-900">{review.name}</p>
                    {review.date && (
                      <p className="text-sm text-slate-400">{review.date}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area - Updated for National Coverage */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Werkgebied: <span className="text-amber-500">Landelijk Actief</span>
              </h2>
              <p className="text-slate-600 mb-8 text-lg">
                M Pomp Multiservice is gevestigd in IJmuiden, maar ik werk landelijk. Of u nu in de regio IJmond woont of daarbuiten, ik kom graag bij u langs voor installatie, onderhoud of storingen.
              </p>
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-amber-100 rounded-full text-amber-600">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Standplaats IJmuiden</h4>
                    <p className="text-slate-600">Landelijk inzetbaar voor projecten en klussen.</p>
                  </div>
                </div>
                <p className="text-slate-500 italic">
                  Ik ben flexibel en mobiel. Neem contact op om de mogelijkheden te bespreken.
                </p>
              </div>
            </div>
            <div className="relative h-[400px] bg-slate-50 rounded-2xl overflow-hidden border-4 border-slate-50 shadow-xl">
               {/* Google Maps Embed */}
               <iframe 
                 src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2430.569851659972!2d4.609594076709841!3d52.463581172045586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c5ef0c8a5a5a5d%3A0x1c8b8b8b8b8b8b8b!2sIJsselstraat%2010%2C%201972%20WD%20IJmuiden!5e0!3m2!1snl!2snl!4v1700000000000!5m2!1snl!2snl" 
                 width="100%" 
                 height="100%" 
                 style={{ border: 0 }} 
                 allowFullScreen={true} 
                 loading="lazy" 
                 referrerPolicy="no-referrer-when-downgrade"
                 title="Locatie M Pomp Multiservice"
               ></iframe>
               <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-slate-100">
                  <p className="font-bold text-slate-900">Hoofdkantoor</p>
                  <p className="text-slate-600">IJsselstraat 10, 1972 WD IJmuiden</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-amber-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Heeft u een installateur nodig?</h2>
          <p className="text-slate-900/80 text-xl mb-8 max-w-2xl mx-auto font-medium">
            Ik ben 7 dagen per week bereikbaar voor advies, installatie en onderhoud.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
             {/* Bottom Phone Button - explicit onClick */}
             <button 
              onClick={() => window.location.href = 'tel:+31625007564'} 
              className={cn(
                buttonVariants({ size: "lg" }),
                "bg-slate-900 hover:bg-slate-800 text-white font-bold h-14 px-8 text-lg border-2 border-transparent flex items-center gap-2 cursor-pointer border-none"
              )}
             >
                <Phone className="h-5 w-5" />
                Bel Nu: 06 25007564
            </button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold h-14 px-8 text-lg cursor-pointer">
              <Link to="/contact">Neem Contact Op</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
